import React, { useEffect, useRef } from "react";
import { clsx } from "clsx";

interface ProgressProps extends React.HTMLAttributes<HTMLDivElement> {
  value: number;
  max?: number;
  variant?: "primary" | "success" | "warning";
  size?: "sm" | "md" | "lg";
  showLabel?: boolean;
}

const Progress = React.forwardRef<HTMLDivElement, ProgressProps>(
  (
    {
      className,
      value: rawValue,
      max = 100,
      variant = "primary",
      size = "md",
      showLabel = false,
      ...props
    },
    ref,
  ) => {
    const clampedValue = Math.max(0, Math.min(rawValue, max));
    const percentage = (clampedValue / max) * 100;
    const fillRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
      if (fillRef.current) {
        fillRef.current.style.width = `${percentage}%`;
      }
    }, [percentage]);

    const variantClasses = {
      primary: "bg-primary-500",
      success: "bg-success-500",
      warning: "bg-warning-500",
    };

    const sizeClasses = {
      sm: "h-1",
      md: "h-2.5",
      lg: "h-4",
    };

    return (
      <div
        className={clsx("w-full", className)}
        {...props}
        data-testid="progress-wrapper"
      >
        <div
          ref={ref}
          role="progressbar"
          aria-valuenow={clampedValue}
          aria-valuemin={0}
          aria-valuemax={max}
          className={clsx(
            "w-full bg-secondary-200 rounded-full overflow-hidden",
            sizeClasses[size],
          )}
        >
          <div
            ref={fillRef}
            data-testid="progress-fill"
            className={clsx(
              "h-full rounded-full transition-all duration-300",
              variantClasses[variant],
            )}
          />
        </div>
        {showLabel && (
          <span className="block text-sm text-secondary-600 mt-1">
            {Math.round(percentage)}%
          </span>
        )}
      </div>
    );
  },
);
Progress.displayName = "Progress";

export { Progress };
export type { ProgressProps };
